import { useNavigate } from 'react-router-dom';

import { appConfig } from '../configs/app.config';
import { REDIRECT_URL_KEY } from '../constants/app.constant';
import { useAppDispatch } from '../features';
import { apiSlice } from '../features/slice/api-slice';
import {
    clearAuthTokens,
    removeUser,
    signOutSuccess,
} from '../features/slice/auth';
import {
    useLoginMutation,
    useLogoutMutation,
} from '../features/slice/auth/session-api';
import type { SignInCredential } from '../types/auth';
import { isErrorWithMessage } from '../utils/response-error';
import { useQuery } from './use-query';

type Status = 'success' | 'failed';

export const useAuth = () => {
    const [
        login,
        { data, isLoading, error: responseError, isSuccess, isError },
    ] = useLoginMutation();
    const [logout, { isLoading: isSigningOut }] = useLogoutMutation();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const query = useQuery();

    const signIn = async (
        values: SignInCredential,
    ): Promise<
        | {
              status: Status;
              message: string;
          }
        | undefined
    > => {
        try {
            const resp = await login(values);
            if (!resp.error) {
                const redirectUrl = query.get(REDIRECT_URL_KEY);
                navigate(
                    redirectUrl
                        ? redirectUrl
                        : appConfig.authenticatedEntryPath,
                );
                return {
                    status: 'success',
                    message: 'You have successfully loged-in!',
                };
            }
            return {
                status: 'failed',
                message: 'Something went wrong',
            };
        } catch (errors: any) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            };
        }
    };
    const signOut = async () => {
        try {
            await logout().unwrap();
        } catch (error) {
            console.error('Failed to invalidate the server session:', error);
        } finally {
            clearAuthTokens();
            dispatch(signOutSuccess());
            dispatch(removeUser());
            dispatch(apiSlice.util.resetApiState());
            navigate(appConfig.unAuthenticatedEntryPath, { replace: true });
        }
    };
    return {
        authenticated: false,
        signIn,
        isLoading,
        isError,
        data,
        apiError: isErrorWithMessage(responseError),
        isSuccess,
        signOut,
        isSigningOut,
    };
};
